/*
 * Selbst gehostete Protomaps-Vector-Tiles: der go-pmtiles serve-Container
 * auf tiles.hvacloud.at exponiert unser Europe-pmtiles-Archiv als Standard-
 * XYZ-URL, MapLibre-GL nutzt die direkt ohne zusaetzlichen Protokoll-Handler.
 *
 * Fuer die Styles greifen wir aufs offizielle @protomaps/basemaps npm-Paket
 * zurueck — das liefert nur das layers-Array, wir kombinieren es mit den
 * Sources, Glyphs und Sprites zu einem vollstaendigen MapLibre-StyleSpec.
 *
 * Glyphs + Sprites kommen zunaechst von protomaps.github.io (Assets-Repo des
 * OSS-Projekts, dafuer gedacht kostenlos konsumiert zu werden). Wenn wir
 * spaeter komplett aus dem eigenen Runtime-Pfad servieren wollen, ziehen wir
 * die Assets ins frontend/public und schalten die URLs um.
 */
import { layers, namedFlavor } from '@protomaps/basemaps';
import type { StyleSpecification } from 'maplibre-gl';

/** Hostname des selbst gehosteten pmtiles-Servers (Traefik → go-pmtiles).
 *  In Dev wird das ueber Compose gestartet, in Prod das gleiche Setup. */
const TILE_HOST = 'https://tiles.hvacloud.at';

/** Name des pmtiles-Archivs innerhalb des Volumes. Der go-pmtiles serve-
 *  Command exponiert `/data/europe.pmtiles` als `/europe/{z}/{x}/{y}.mvt`. */
const ARCHIVE_NAME = 'europe';

/** Base-URL fuer Glyphs (Fonts) und Sprites (Icons). Protomaps hostet die
 *  Basemap-Assets als OSS-CDN unter protomaps.github.io — legitim, dafuer
 *  vorgesehen und aendert sich nur zwischen Major-Versionen. */
const ASSETS_BASE = 'https://protomaps.github.io/basemaps-assets';
const SPRITE_VERSION = 'v4';

/** Baut das komplette StyleSpec fuer ein bestimmtes Basemap-Theme.
 *
 *  `flavor` entspricht den offiziellen Protomaps-Themes:
 *    - `light`  : Positron-artig, hell + neutral, gute Kartografie
 *    - `dark`   : Dark-Matter-artig, dunkler Slate-Ton, kein Overlay-Feuer
 *    - `white`  / `black`: extrem reduzierte Varianten
 *    - `grayscale`: monochrom mit unterschiedlichen Grauwerten
 *
 *  Wir nutzen `light`/`dark` als Direktentsprechung zum alten Carto-Look.
 */
export function buildMapStyle(flavor: 'light' | 'dark'): StyleSpecification {
  return {
    version: 8,
    glyphs: `${ASSETS_BASE}/fonts/{fontstack}/{range}.pbf`,
    sprite: `${ASSETS_BASE}/sprites/${SPRITE_VERSION}/${flavor}`,
    sources: {
      protomaps: {
        type: 'vector',
        tiles: [`${TILE_HOST}/${ARCHIVE_NAME}/{z}/{x}/{y}.mvt`],
        // Protomaps-Basemaps-Tiles sind bis Zoom 15 pre-generiert; MapLibre
        // ueber-zoomt darueber hinaus automatisch durch Skalierung der
        // Zoom-15-Tile.
        maxzoom: 15,
        attribution:
          '<a href="https://protomaps.com" target="_blank" rel="noopener">Protomaps</a>' +
          ' © <a href="https://openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>',
      },
    },
    // layers() erwartet den Source-Name als ersten Parameter, damit die
    // Layer-Definitionen ihre `source: 'protomaps'`-Referenz bekommen.
    // `lang: 'de'` zieht deutsche Labels vor, mit englischen als Fallback.
    layers: layers('protomaps', namedFlavor(flavor), { lang: 'de' }),
  };
}
